/**
 * Integrity tab → WiP ↔ QuickBooks reconciliation. Cross-matches
 * INVOICED WiP instalments against actual QBO invoices by master
 * project number + amount (client-name fallback for rows without
 * one) and lists the leakage in both directions: instalments marked
 * invoiced with no matching invoice, and invoices raised outside
 * the WiP ledger. Foreign-currency invoices are shown in GBP at
 * each invoice's own exchange rate.
 */
import { fmtGbpShort, PanelTitle } from "../staff/CommissionedPanels";
import { TabState, Card, KpiStrip, InfoTip } from "./cfoShared";
import { useQboData, QboHeader, fmtGbp } from "./qboShared";

export default function QboReconciliationPanel() {
  const { data, loading, error, refreshing, reload } = useQboData("reconciliation");
  if (loading || error) {
    return <TabState loading={loading} error={error} testid="qbo-recon-state" />;
  }
  const k = data.kpis || {};

  return (
    <div className="flex flex-col gap-4" data-testid="qbo-recon-panel">
      <div className="flex items-center justify-between flex-wrap gap-2 mt-2">
        <PanelTitle>
          WiP ↔ QuickBooks reconciliation <InfoTip id="recon_match" />
        </PanelTitle>
        <QboHeader data={data} refreshing={refreshing} onRefresh={reload} testid="qbo-recon-refresh-btn" />
      </div>

      {data.note && (
        <p
          className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-[11px] text-sky-800"
          data-testid="qbo-recon-note"
        >
          {data.note}
        </p>
      )}

      {typeof k.fx_missing_rate === "number" &&
        (k.fx_missing_rate > 0 ? (
          <p
            className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800"
            data-testid="qbo-recon-fx-missing-warning"
          >
            {k.fx_missing_rate} foreign currency invoice{k.fx_missing_rate === 1 ? "" : "s"} carry no
            exchange rate in QuickBooks and are counted at face value:{" "}
            {(data.fx_missing || [])
              .slice(0, 8)
              .map((f) => `#${f.doc_number} (${f.currency} ${Number(f.face_value).toLocaleString()})`)
              .join(", ")}
            {k.fx_missing_rate > 8 ? ` and ${k.fx_missing_rate - 8} more` : ""}
          </p>
        ) : (
          <p className="text-[11px] text-teal-700" data-testid="qbo-recon-fx-ok">
            Every foreign currency invoice carries an exchange rate; all figures below are true GBP.
          </p>
        ))}

      <KpiStrip
        cards={[
          {
            label: "QBO invoices tied to WiP",
            value: `${k.matched} / ${k.qbo_invoices}`,
            sub: `${k.match_rate_pct || 0}% · ${fmtGbpShort(k.matched_value)} tied to WiP`,
            tone: k.match_rate_pct >= 80 ? "text-teal-700" : "text-slate-900",
            testid: "qbo-recon-kpi-matched",
          },
          {
            label: "WiP invoiced · no invoice found",
            value: (k.wip_unmatched_rows ?? Math.max(k.wip_invoiced_rows - k.matched, 0)).toLocaleString(),
            sub: `${fmtGbpShort(k.wip_unmatched_value)} unexplained`,
            tone: k.wip_unmatched_value > 0 ? "text-amber-700" : "text-teal-700",
            testid: "qbo-recon-kpi-wip-unmatched",
          },
          {
            label: "Invoices outside the WiP",
            value: (k.qbo_invoices - k.matched).toLocaleString(),
            sub: `${fmtGbpShort(k.qbo_unmatched_value)} net`,
            tone: k.qbo_unmatched_value > 0 ? "text-amber-700" : "text-teal-700",
            testid: "qbo-recon-kpi-qbo-unmatched",
          },
        ]}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card testid="qbo-recon-wip-unmatched">
          <PanelTitle>WiP instalments with no QBO invoice · leakage candidates</PanelTitle>
          {(data.wip_unmatched || []).length === 0 ? (
            <p className="text-xs text-stone-400 py-2">Every invoiced instalment has a matching invoice.</p>
          ) : (
            <ul className="divide-y divide-stone-50 mt-2 max-h-80 overflow-y-auto pr-1">
              {data.wip_unmatched.map((w, idx) => (
                <li key={`${w.fiid}-${idx}`} className="py-2 flex items-center gap-3" data-testid={`qbo-recon-wip-${w.fiid}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-slate-800 truncate">
                      {w.client}
                      <span className="text-stone-400 font-normal"> · {w.project_number || w.project_name || w.fiid}</span>
                    </p>
                    <p className="text-[10px] font-mono text-stone-400">
                      {w.doi || "no DOI"} · {w.status}
                    </p>
                  </div>
                  <span className="text-xs font-mono tabular-nums font-semibold text-amber-700 shrink-0">
                    {fmtGbp(w.value)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card testid="qbo-recon-qbo-unmatched">
          <PanelTitle>QBO invoices not on the WiP ledger</PanelTitle>
          {(data.qbo_unmatched || []).length === 0 ? (
            <p className="text-xs text-stone-400 py-2">Every invoice ties back to a WiP instalment.</p>
          ) : (
            <ul className="divide-y divide-stone-50 mt-2 max-h-80 overflow-y-auto pr-1">
              {data.qbo_unmatched.map((i) => (
                <li key={i.id} className="py-2 flex items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-slate-800 truncate">
                      {i.customer}
                      {i.doc_number && <span className="text-stone-400 font-normal"> · #{i.doc_number}</span>}
                    </p>
                    <p className="text-[10px] font-mono text-stone-400">{i.date}</p>
                  </div>
                  <span className="text-xs font-mono tabular-nums text-slate-900 shrink-0">
                    {fmtGbp(i.net)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}
